import { forwardRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import {
  generateArc,
  createArcMesh,
  greatCircleNormal,
  intersectTwoDistanceCirclesOnSphere,
  findPAPTangentsOnPinBuffer,
  orientNormalTowardTarget,
  isValidPALLayout,
} from "../3d/utils/layoutUtils";
import { addTextLabel, drawFingerRing } from "../3d/utils/threeUtils";
import { apiConfig } from "../api/api";
import { useSafeTexture } from "../../hooks/useSafeTexture";

const ballRadius = 4.294;
const arcDistancePinToMB = 6.75;
const arcOffset = 0.01;

/**
 * Small-circle arc around an axis, centered at `midPointOnRing`,
 * with a given arc length (inches) on that ring.
 */
function drawSmallCircleArcAroundAxis(options: {
  ball: THREE.Object3D,
  midPointOnRing: THREE.Vector3,
  axis: THREE.Vector3,
  ringRadiusInches: number,
  arcLengthInches: number,
  color: number,
  segments?: number,
}) {
  const {
    ball,
    midPointOnRing,
    axis,
    ringRadiusInches,
    arcLengthInches,
    color,
    segments = 128,
  } = options;

  const ringTheta = ringRadiusInches / ballRadius;
  const sinRingTheta = Math.sin(ringTheta);
  if (sinRingTheta <= 1e-6 || arcLengthInches <= 0) return;

  // s = R * sin(theta) * dPhi  =>  dPhi = s / (R * sin(theta))
  const totalPhi = arcLengthInches / (ballRadius * sinRingTheta);

  const arcPoints = generateArc({
    center: midPointOnRing.clone().normalize().multiplyScalar(ballRadius),
    axis: axis.clone().normalize(),
    radius: ballRadius,
    angleSweep: totalPhi,
    handednessFactor: 1,
    arcOffset,
    segments,
  });

  ball.add(createArcMesh(arcPoints, color));
}

function getFingerRadii(spanMeasurements: {
  middleFingerDiameter?: number,
  ringFingerDiameter?: number,
}) {
  const defaultRadius = 0.5;
  const safeRadius = (diameter?: number) =>
    diameter && diameter > 0 ? diameter / 2 : defaultRadius;

  return {
    middle: safeRadius(spanMeasurements?.middleFingerDiameter),
    ring: safeRadius(spanMeasurements?.ringFingerDiameter),
  };
}

function mirrorAcrossPlane(dir: THREE.Vector3, planeNormalUnit: THREE.Vector3) {
  const dot = dir.dot(planeNormalUnit);
  return dir
    .clone()
    .sub(planeNormalUnit.clone().multiplyScalar(2 * dot))
    .normalize();
}

function solvePalLayout(options: {
  papDir: THREE.Vector3,
  planeNormal: THREE.Vector3,
  tangentDir: THREE.Vector3,
  midlineTangent: THREE.Vector3,
  papVerticalOffset: number,
  verticalSign: number,
  requiredHorizontal: number,
}) {
  const {
    papDir,
    planeNormal,
    tangentDir,
    midlineTangent,
    papVerticalOffset,
    verticalSign,
    requiredHorizontal,
  } = options;

  const thetaVertical = papVerticalOffset / ballRadius;
  const baseOffsetDir =
    thetaVertical > 1e-6
      ? papDir
          .clone()
          .applyAxisAngle(midlineTangent, verticalSign * thetaVertical)
          .normalize()
      : null;

  const offsetDirFromPhi =
    baseOffsetDir === null
      ? null
      : (phi: number) =>
          baseOffsetDir.clone().applyAxisAngle(papDir, phi).normalize();

  const maxLightningTheta = papDir.angleTo(tangentDir);
  const pointOnLightning = (theta: number) =>
    papDir
      .clone()
      .applyAxisAngle(planeNormal, theta)
      .normalize()
      .multiplyScalar(ballRadius);

  let papOffsetPoint: THREE.Vector3 | null = null;
  let papOffsetPhi: number | null = null;
  let lightningTheta = maxLightningTheta;
  let lightningEnd: THREE.Vector3 | null = null;
  let hasPerpendicularSolution = false;
  let solutionIsAccurate = false;
  let selectedPalPlaneNormal: THREE.Vector3 | null = null;

  if (papVerticalOffset > 0 && requiredHorizontal > 0 && offsetDirFromPhi) {
    const distanceTolerance = 0.02;
    const evaluateCandidate = (phi: number) => {
      const offsetDir = offsetDirFromPhi(phi);
      let offsetCircleTangent = papDir.clone().cross(offsetDir);
      if (offsetCircleTangent.lengthSq() < 1e-8) return null;
      offsetCircleTangent.normalize();

      let palPlaneNormal = offsetDir.clone().cross(offsetCircleTangent);
      if (palPlaneNormal.lengthSq() < 1e-8) return null;
      palPlaneNormal.normalize();
      let palTangentDir = palPlaneNormal.clone().cross(offsetDir);
      if (palTangentDir.lengthSq() < 1e-8) return null;
      palTangentDir.normalize();
      if (palTangentDir.dot(offsetCircleTangent) < 0) {
        palPlaneNormal.multiplyScalar(-1);
        palTangentDir.multiplyScalar(-1);
      }

      const intersectionAxis = planeNormal.clone().cross(palPlaneNormal);
      if (intersectionAxis.lengthSq() < 1e-8) return null;
      intersectionAxis.normalize();

      const evaluateIntersectionDir = (candidate: THREE.Vector3) => {
        const tangential = candidate
          .clone()
          .sub(offsetDir.clone().multiplyScalar(offsetDir.dot(candidate)));
        if (tangential.lengthSq() < 1e-8) return null;
        const tangentAlignment = palTangentDir.dot(tangential.normalize());
        return {
          dir: candidate.clone(),
          tangentAlignment,
          greenAlignment: candidate.angleTo(tangentDir),
        };
      };

      const candidateInfos = [
        evaluateIntersectionDir(intersectionAxis.clone()),
        evaluateIntersectionDir(intersectionAxis.clone().multiplyScalar(-1)),
      ].filter(Boolean);

      if (candidateInfos.length === 0) return null;

      candidateInfos.sort((a, b) => {
        const angleDiff = a.greenAlignment - b.greenAlignment;
        if (Math.abs(angleDiff) > 1e-5) return angleDiff;
        return b.tangentAlignment - a.tangentAlignment;
      });

      const intersectionDir = candidateInfos[0].dir.clone();
      const lightningAngle = papDir.angleTo(intersectionDir);
      const distanceAngle = offsetDir.angleTo(intersectionDir);

      return {
        offsetDir,
        intersectionDir,
        phi,
        distance: distanceAngle * ballRadius,
        distanceError: Math.abs(
          distanceAngle * ballRadius - requiredHorizontal
        ),
        lightningAngle,
        palPlaneNormal,
      };
    };

    const phiSamples = 720;
    let bestCandidate = null;
    let bestError = Infinity;

    for (let i = 0; i < phiSamples; i++) {
      const phi = (i / phiSamples) * 2 * Math.PI - Math.PI;
      const candidate = evaluateCandidate(phi);
      if (!candidate) continue;
      if (candidate.distanceError < bestError) {
        bestCandidate = candidate;
        bestError = candidate.distanceError;
        if (candidate.distanceError <= distanceTolerance) break;
      }
    }

    if (bestCandidate) {
      let refined = { ...bestCandidate };
      let step = (2 * Math.PI) / (phiSamples * 2);
      for (let iter = 0; iter < 25; iter++) {
        const left = evaluateCandidate(refined.phi - step);
        const right = evaluateCandidate(refined.phi + step);
        let improved = false;
        if (left && left.distanceError < refined.distanceError) {
          refined = left;
          improved = true;
        }

        if (right && right.distanceError < refined.distanceError) {
          refined = right;
          improved = true;
        }
        if (!improved) step *= 0.5;
        if (step < 1e-4) break;
      }

      if (refined.distanceError > distanceTolerance) {
        console.warn(
          `PAL perpendicular approximation: distance error=${refined.distanceError.toFixed(
            3
          )}`
        );
      }

      papOffsetPoint = refined.offsetDir.clone().multiplyScalar(ballRadius);
      papOffsetPhi = refined.phi;
      lightningTheta = refined.lightningAngle;
      lightningEnd = refined.intersectionDir.clone().multiplyScalar(ballRadius);
      selectedPalPlaneNormal = refined.palPlaneNormal.clone();
      hasPerpendicularSolution = true;
      solutionIsAccurate = refined.distanceError <= distanceTolerance;
    } else {
      console.warn("PAL perpendicular solver: unable to find valid candidate.");
    }
  }

  if (!papOffsetPoint && papVerticalOffset > 0 && offsetDirFromPhi) {
    papOffsetPoint = offsetDirFromPhi(0).multiplyScalar(ballRadius);
    papOffsetPhi = 0;
  }

  if (!lightningEnd) {
    lightningEnd = pointOnLightning(maxLightningTheta);
    lightningTheta = maxLightningTheta;
  }

  return {
    offsetDirFromPhi,
    papOffsetPoint,
    papOffsetPhi,
    lightningEnd,
    lightningTheta,
    hasPerpendicularSolution,
    solutionIsAccurate,
    selectedPalPlaneNormal,
    thetaVertical,
  };
}

// ------------------------------
// Component
// ------------------------------

export const BowlingLayoutPAL = forwardRef((props: any, ref) => {
  const {
    pinToPapDistance,
    mbToPapDistance,
    pinToCenter,
    playerPapOffset,
    spanMeasurements,
    cameraPosition,
    ballId,
    access_token,
    onTextureStatusChange,
    onLayoutError,
  } = props;

  const handednessFactor = spanMeasurements?.isLeftHanded ? -1 : 1;
  const fingerRadii = getFingerRadii(spanMeasurements);

  // Ball mesh + texture
  const imageTextureUrl = ballId && access_token
    ? `${apiConfig.apiServer}ballDrilled/${ballId}/texture?access_token=${access_token}`
    : null;
  
  const { texture, isLoading } = useSafeTexture(imageTextureUrl, onTextureStatusChange);

  // Verificar si playerPapOffset tiene valores numéricos válidos
  const hasValidPapOffset = useMemo(() => {
    const v = playerPapOffset?.vertical;
    const h = playerPapOffset?.horizontal;
    return (
      typeof v === "number" &&
      !isNaN(v) &&
      v > 0 &&
      typeof h === "number" &&
      !isNaN(h) &&
      h > 0
    );
  }, [playerPapOffset]);

  const layoutIsValid = useMemo(() => hasValidPapOffset && isValidPALLayout({
    pinToPapDistance,
    mbToPapDistance,
    pinToCenter,
    playerPapOffset,
  }), [pinToPapDistance, mbToPapDistance, pinToCenter, playerPapOffset, hasValidPapOffset]);

  // Notificar error de layout al padre
  useEffect(() => {
    onLayoutError?.(!layoutIsValid);
  }, [layoutIsValid, onLayoutError]);

  const ballGeometry = new THREE.SphereGeometry(ballRadius, 256, 256);
  const ballMaterial = new THREE.MeshStandardMaterial({
    map: texture,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.2,
  });
  const ball = new THREE.Mesh(ballGeometry, ballMaterial);

  const makeMarker = (pos: THREE.Vector3, color: number) => {
    const radius = 0.15;
    const geometry = new THREE.CircleGeometry(radius, 32);
    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(pos);
    marker.lookAt(pos.clone().add(pos.clone().normalize()));
    ball.add(marker);
  };

  // ------------------------------
  // Core markers: Pin, CG, PSA, PAP
  // ------------------------------

  const pin = new THREE.Vector3(0, ballRadius, 0);
  makeMarker(pin, 0xff0000);

  const axis = new THREE.Vector3(1, 0, 0);
  const thetaPinToMB = arcDistancePinToMB / ballRadius;

  const mb = pin
    .clone()
    .applyAxisAngle(axis, handednessFactor * thetaPinToMB)
    .normalize()
    .multiplyScalar(ballRadius);

  const cg = pin
    .clone()
    .applyAxisAngle(axis, (handednessFactor * thetaPinToMB) / 2)
    .normalize()
    .multiplyScalar(ballRadius);

  addTextLabel({
    text: "CG",
    position: cg,
    normal: cg.clone().normalize(),
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  addTextLabel({
    text: "PSA",
    position: mb,
    normal: mb.clone().normalize(),
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  // Si el layout no es válido, mostrar solo la bola con marcadores básicos
  if (!layoutIsValid) {
    return (
      <group ref={ref} position={[0, 0, 0]}>
        <primitive object={ball} />
      </group>
    );
  }

  // PAP from distances to Pin and PSA
  const { q1: papCandidate1, q2: papCandidate2 } =
    intersectTwoDistanceCirclesOnSphere(
      pin,
      mb,
      pinToPapDistance,
      mbToPapDistance,
      ballRadius
    );

  let pap: THREE.Vector3 | null = null;
  if (papCandidate1 && papCandidate2) {
    if (handednessFactor === 1) {
      pap = papCandidate1.clone();
    } else {
      pap = papCandidate2.clone();
    }
  }

  if (!pap) {
    return (
      <group ref={ref} position={[0, 0, 0]}>
        <primitive object={ball} />
      </group>
    );
  }

  addTextLabel({
    text: "PAP",
    position: pap,
    normal: pap.clone().normalize(),
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  // Arcs Pin→PAP and MB→PAP (simple visuals)
  const pinNormal = pin.clone().normalize();
  const arcPointsPinToPap = generateArc({
    center: pap,
    axis: pinNormal,
    radius: ballRadius,
    angleSweep: 2 / ballRadius,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPointsPinToPap, 0xffffff));

  const mbNormal = mb.clone().normalize();
  const toPAP = pap.clone().sub(mb).normalize();
  const projectedDirMB = toPAP
    .clone()
    .sub(mbNormal.clone().multiplyScalar(toPAP.dot(mbNormal)))
    .normalize();
  const mbBufferAngle = mbToPapDistance / ballRadius;
  const mbArcCenter = mb
    .clone()
    .applyAxisAngle(
      mbNormal.clone().cross(projectedDirMB).normalize(),
      mbBufferAngle
    )
    .normalize()
    .multiplyScalar(ballRadius);

  const arcPointsMbToPap = generateArc({
    center: mbArcCenter,
    axis: mbNormal,
    radius: ballRadius,
    angleSweep: 2 / ballRadius,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPointsMbToPap, 0xffffff));

  // ------------------------------
  // Pin buffer & tangent point
  // ------------------------------

  const pinBufferDistance = pinToCenter;
  const pinDir = pin.clone().normalize();

  const { rightTangent, leftTangent } = findPAPTangentsOnPinBuffer({
    pin,
    pap,
    pinBufferDistance,
    ballRadius,
  });

  const tangentPoint = spanMeasurements.isLeftHanded
    ? rightTangent || leftTangent
    : leftTangent || rightTangent;

  if (tangentPoint) {
    // Small arc around Pin at tangent (2" total)
    drawSmallCircleArcAroundAxis({
      ball,
      midPointOnRing: tangentPoint,
      axis: pinDir,
      ringRadiusInches: pinBufferDistance,
      arcLengthInches: 4.0,
      color: 0xffffff,
    });

    // ------------------------------
    // Lightning line (2LS-style) from PAP along tangency great-circle
    // ------------------------------
    const papDir = pap.clone().normalize();
    let planeNormal = greatCircleNormal(
      papDir,
      tangentPoint.clone().normalize()
    );
    planeNormal = orientNormalTowardTarget(
      papDir,
      tangentPoint.clone().normalize(),
      planeNormal
    );

    const planeNormalUnit = planeNormal.clone().normalize();
    const mirrorAcrossGreenPlane = (dir: THREE.Vector3) =>
      mirrorAcrossPlane(dir, planeNormalUnit);
    const midlineTangent = planeNormal.clone().cross(papDir).normalize();
    const tangentDir = tangentPoint.clone().normalize();

    const papVerticalOffset = Math.max(playerPapOffset.vertical, 0);
    const totalHorizontalDistance = Math.max(playerPapOffset.horizontal, 0);
    const papHorizontalOffset = Math.max(totalHorizontalDistance - 0.5, 0);
    const papDirectionRaw = (playerPapOffset.direction || "Down").toLowerCase();
    const directionIsDown = papDirectionRaw === "down";
    const verticalSign = directionIsDown ? 1 : -1;

    const palSolution = solvePalLayout({
      papDir,
      planeNormal,
      tangentDir,
      midlineTangent,
      papVerticalOffset,
      verticalSign,
      requiredHorizontal: papHorizontalOffset,
    });

    let {
      offsetDirFromPhi,
      papOffsetPoint,
      papOffsetPhi,
      lightningEnd,
      lightningTheta,
      hasPerpendicularSolution,
      selectedPalPlaneNormal,
      thetaVertical,
    } = palSolution;
    let palArcAxis: THREE.Vector3 | null = null;
    let centerOfGripPoint: THREE.Vector3 | null = null;

    if (!directionIsDown) {
      if (offsetDirFromPhi) {
        const originalFn = offsetDirFromPhi;
        offsetDirFromPhi = (phi: number) =>
          mirrorAcrossGreenPlane(originalFn(phi));
      }

      if (papOffsetPoint) {
        const mirrored = mirrorAcrossGreenPlane(
          papOffsetPoint.clone().normalize()
        );
        papOffsetPoint = mirrored.multiplyScalar(ballRadius);
      }

      if (lightningEnd) {
        const mirrored = mirrorAcrossGreenPlane(
          lightningEnd.clone().normalize()
        );
        lightningEnd = mirrored.multiplyScalar(ballRadius);
      }

      if (selectedPalPlaneNormal) {
        selectedPalPlaneNormal = mirrorAcrossGreenPlane(
          selectedPalPlaneNormal.clone()
        );
      }
    }

    const lightningVisualExtension = 1 / ballRadius;
    const renderLightningTheta = lightningTheta + lightningVisualExtension;
    const papLightningArc = generateArc({
      center: papDir
        .clone()
        .applyAxisAngle(planeNormal, renderLightningTheta / 2)
        .normalize()
        .multiplyScalar(ballRadius),
      axis: planeNormal,
      radius: ballRadius,
      angleSweep: renderLightningTheta,
      handednessFactor: 1,
      arcOffset,
      segments: 256,
    });
    ball.add(createArcMesh(papLightningArc, 0xffffff));

    let valPlaneNormal = papDir.clone().cross(midlineTangent).normalize();
    if (selectedPalPlaneNormal) {
      const palTangentAtPap = selectedPalPlaneNormal.clone().cross(papDir);
      if (palTangentAtPap.lengthSq() > 1e-8) {
        valPlaneNormal = palTangentAtPap.clone().normalize();
      }
    }
    const verticalDirectionFactor = directionIsDown ? 1 : -1;
    valPlaneNormal.multiplyScalar(verticalDirectionFactor);

    const valSegments = 256;
    const valLengthInches = Math.max(papVerticalOffset + 1, 1);
    const valSweep = Math.min(valLengthInches / ballRadius, Math.PI);
    const valPoints: THREE.Vector3[] = [];
    for (let i = 0; i <= valSegments; i++) {
      const angle = valSweep * (i / valSegments - 0.5) * 2;
      const dir = papDir
        .clone()
        .applyAxisAngle(valPlaneNormal, angle)
        .normalize()
        .multiplyScalar(ballRadius + arcOffset);
      valPoints.push(dir);
    }
    ball.add(createArcMesh(valPoints, 0xffffff));

    if (papOffsetPoint && papVerticalOffset > 0) {
      if (thetaVertical > 1e-6 && offsetDirFromPhi) {
        const sinTheta = Math.sin(thetaVertical);
        if (sinTheta > 1e-6) {
          const arcHalfLength = 2;
          const halfPhi = arcHalfLength / (ballRadius * sinTheta);
          const clampedHalfPhi = Math.min(Math.max(halfPhi, 0), Math.PI);
          const offsetArcPoints: THREE.Vector3[] = [];
          const arcSegments = 128;
          for (let i = 0; i <= arcSegments; i++) {
            const t = (i / arcSegments - 0.5) * 2;
            const phi = (papOffsetPhi ?? 0) + t * clampedHalfPhi;
            const dir = offsetDirFromPhi(phi);
            offsetArcPoints.push(
              dir.clone().multiplyScalar(ballRadius + arcOffset)
            );
          }
          ball.add(createArcMesh(offsetArcPoints, 0xffffff));
        }
      }

      if (hasPerpendicularSolution && lightningEnd) {
        const offsetDir = papOffsetPoint.clone().normalize();
        const lightningDir = lightningEnd.clone().normalize();
        let offsetToLightningNormal = greatCircleNormal(
          offsetDir,
          lightningDir
        );
        offsetToLightningNormal = orientNormalTowardTarget(
          offsetDir,
          lightningDir,
          offsetToLightningNormal
        );

        const targetAngle = papHorizontalOffset / ballRadius;
        const fullAngle =
          totalHorizontalDistance > 0
            ? totalHorizontalDistance / ballRadius
            : targetAngle;
        const arcSweep = targetAngle;
        const midOffsetLightning = offsetDir
          .clone()
          .applyAxisAngle(offsetToLightningNormal, arcSweep / 2)
          .normalize()
          .multiplyScalar(ballRadius);
        const offsetLightningArc = generateArc({
          center: midOffsetLightning,
          axis: offsetToLightningNormal,
          radius: ballRadius,
          angleSweep: arcSweep,
          handednessFactor: 1,
          arcOffset,
          segments: 256,
        });
        ball.add(createArcMesh(offsetLightningArc, 0xffffff));

        if (!palArcAxis) {
          palArcAxis = offsetToLightningNormal.clone().normalize();
        }

        if (totalHorizontalDistance > 0) {
          const gripDir = offsetDir
            .clone()
            .applyAxisAngle(offsetToLightningNormal, fullAngle)
            .normalize();
          centerOfGripPoint = gripDir.clone().multiplyScalar(ballRadius);
        }
      }
    }

    // ------------------------------
    // Fingers along PAP horizontal coordinate
    // ------------------------------
    let gripCenterDir: THREE.Vector3 | null = null;
    let gripHorizontalAxis: THREE.Vector3 | null = null;
    if (papOffsetPoint) {
      const targetDir = papOffsetPoint.clone().normalize();
      const centerDir = centerOfGripPoint
        ? centerOfGripPoint.clone().normalize()
        : lightningEnd?.clone().normalize();
      if (centerDir) {
        gripCenterDir = centerDir.clone();
      }
      let axisLT = palArcAxis ? palArcAxis.clone().normalize() : null;
      if (centerDir && !axisLT) {
        axisLT = greatCircleNormal(centerDir, targetDir);
        axisLT = orientNormalTowardTarget(centerDir, targetDir, axisLT);
      }
      if (centerDir && axisLT) {
        gripHorizontalAxis = axisLT.clone().normalize();
        const baseOffset = 0.125;
        const middleOffsetAngle =
          (baseOffset + fingerRadii.middle) / ballRadius;
        const ringOffsetAngle = (baseOffset + fingerRadii.ring) / ballRadius;

        const holeCenterDir1 = centerDir
          .clone()
          .applyAxisAngle(axisLT, middleOffsetAngle)
          .normalize();
        const holeCenter1 = holeCenterDir1
          .clone()
          .multiplyScalar(ballRadius + arcOffset);

        const holeCenterDir2 = centerDir
          .clone()
          .applyAxisAngle(axisLT, -ringOffsetAngle)
          .normalize();
        const holeCenter2 = holeCenterDir2
          .clone()
          .multiplyScalar(ballRadius + arcOffset);

        drawFingerRing(
          ball,
          holeCenter1,
          fingerRadii.middle,
          ballRadius,
          arcOffset,
          0xffffff
        );
        drawFingerRing(
          ball,
          holeCenter2,
          fingerRadii.ring,
          ballRadius,
          arcOffset,
          0xffffff
        );
      }
    }

    if (gripCenterDir && cameraPosition) {
      const fromCenterToGrip = gripCenterDir.clone().normalize();
      const fromCenterToCamera = new THREE.Vector3()
        .fromArray(cameraPosition)
        .normalize();
      if (
        fromCenterToGrip.lengthSq() > 1e-8 &&
        fromCenterToCamera.lengthSq() > 1e-8
      ) {
        const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
          fromCenterToGrip,
          fromCenterToCamera
        );
        ball.quaternion.premultiply(rotationQuat);

        if (gripHorizontalAxis) {
          const verticalDir = gripHorizontalAxis.clone().normalize();
          if (verticalDir.lengthSq() > 1e-8) {
            const camForward = fromCenterToCamera.clone();
            const rotatedVertical = verticalDir
              .clone()
              .applyQuaternion(ball.quaternion);
            const projectedUp = rotatedVertical
              .clone()
              .projectOnPlane(camForward)
              .normalize();
            if (projectedUp.lengthSq() > 1e-8) {
              const screenDown = new THREE.Vector3(0, -1, 0);
              const angle = projectedUp.angleTo(screenDown);
              const cross = new THREE.Vector3().crossVectors(
                projectedUp,
                screenDown
              );
              const sign = Math.sign(cross.dot(camForward));
              const rollQuat = new THREE.Quaternion().setFromAxisAngle(
                camForward,
                angle * sign
              );
              ball.quaternion.premultiply(rollQuat);
            }
          }
        }
      }
    }
  }

  return (
    <group ref={ref} position={[0, 0, 0]}>
      <primitive object={ball} />
    </group>
  );
});
